import { useEffect, useRef, useState } from "react";
import { generatePreview, loadReport, previewError } from "../api/reports";
import type { PreviewArtifact, PreviewReportDetails } from "../types";

export function useReportPreview(reportId: number) {
    const [details, setDetails] = useState<PreviewReportDetails | null>(null);
    const [artifact, setArtifact] = useState<PreviewArtifact | null>(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const active = useRef<AbortController | null>(null);
    useEffect(() => {
        const controller = new AbortController();
        active.current = controller;
        setDetails(null); setArtifact(null); setError("");
        if (!reportId) { setLoading(false); return () => controller.abort(); }
        setLoading(true);
        const timer = window.setTimeout(async () => {
            try {
                const next = await loadReport(reportId, controller.signal);
                if (controller.signal.aborted) return;
                setDetails(next);
                const result = await generatePreview(reportId, controller.signal);
                if (!controller.signal.aborted) setArtifact(result);
            } catch (e) {
                const message = await previewError(e);
                if (!controller.signal.aborted) setError(message);
            } finally { if (!controller.signal.aborted) setLoading(false); }
        }, 0);
        return () => { window.clearTimeout(timer); controller.abort(); };
    }, [reportId, attempt]);
    return { details: details?.report.reportId === reportId ? details : null,
        artifact: artifact?.reportId === reportId ? artifact : null, error, loading, refresh: () => setAttempt(n => n + 1),
        cancel: () => { active.current?.abort(); setLoading(false); setError("Preview cancelled. Select Retry when you are ready."); } };
}
